// interface Product {
//   overview: {
//     name: string;
//     heroImage?: string;
//   };
//   product: {
//     description: string;
//     color?: string;
//     details?: {
//       keywords?: string[];
//       material?: string;
//       materials?: string;
//       Materials?: string;
//       size?: string;
//     };
//   };
//   price?: {
//     min: number;
//     max: number;
//   };

import { ProductObject } from "app/api/product/product.type";
import { UserType } from "app/api/user/user.types";
export const description =
  "Australia’s trusted supplier of affordable promotional products and branded merchandise. Custom printed items for events, trade shows & corporate gifts – fast shipping nationwide.";

// }
interface Data {
  products: ProductObject[];
  productShowCase: {
    category: { name: string };
    _id: string;
    type: string;
    title: string;
    subTitle: string;
    isActive: true;
    products: ProductObject[];
    createdAt: string;
    updatedAt: string;
  }[];
}
export const homePageSchema = (data: Data) => {
  const featuredProducts = {
    "@type": "ItemList",
    name: "Featured Products",
    itemListElement: [] as any[],
  };

  // Build featured products list
  for (let i = 0; i < data.products.length; i++) {
    const product = data.products[i];
    featuredProducts.itemListElement.push({
      "@type": "ListItem",
      position: i + 1,
      item: getProductSchema(product),
    });
  }

  const productShowCaseItems: any[] = [];

  data.productShowCase.forEach((item) => {
    const categoryName = item?.category?.name || item.title || "Uncategorized";
    const products = item.products;
    const itemListElement: any[] = [];
    for (let index = 0; index < products.length; index++) {
      const product = products[index];
      itemListElement.push({
        "@type": "ListItem",
        position: index + 1,
        item: {
          ...getProductSchema(product),
          category: categoryName,
        },
      });
    }

    productShowCaseItems.push({
      "@type": "ItemList",
      name: categoryName,
      itemListElement: itemListElement,
    });
  });

  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Promotional Products Now",
    alternateName: ["Promotional Products", "PPN"],

    url: "https://www.promotionalproductsnow.au/",
    description: description,
    potentialAction: {
      "@type": "SearchAction",
      target: "https://www.promotionalproductsnow.au/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
    mainEntity: [featuredProducts, ...productShowCaseItems],
  };
};
interface Category {
  title: string;
  slug: string;
  description?: string;
}

/**
 * Generates a JSON-LD schema for a category page, listing products and subcategories.
 *
 * When a subcategory page is loaded, the passed in `category` should have its slug
 * already contain the parent path (e.g. "electronics/laptops").
 *
 * @param category - The current category or subcategory details.
 * @param products - An array of products belonging to this category.
 * @param subcategories - An optional array of subcategories under this category.
 * @param footerContent - Optional footer content for the category page.
 * @returns A JSON-LD object representing the category page schema.
 */
export const categoryPageSchema = ({
  category,
  products,
  subcategories = [],
  footerContent,
}: {
  category: Category;
  products: ProductObject[];
    subcategories?: Category[];
    footerContent?: string;
}) => {
  const productList = {
    "@type": "ItemList",
    name: category.title,
    itemListElement: [] as any[],
  };

  products.forEach((product, index) => {
    productList.itemListElement.push({
      "@type": "ListItem",
      position: index + 1,
      item: getProductSchema(product),
    });
  });

  // Extract first paragraph from footer content for description if available
  const enhancedDescription = footerContent
    ? footerContent.split('\n\n').find((p) => p.trim().length > 50) || category.description
    : category.description;

  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: category.title,
    url: `https://www.promotionalproductsnow.au/category/${category.slug}`,
    description: enhancedDescription || category.description,
    ...(footerContent && {
      text: footerContent,
      articleBody: footerContent,
    }),
    potentialAction: {
      "@type": "SearchAction",
      target: "https://www.promotionalproductsnow.au/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
    ...(subcategories && subcategories.length > 0 && {
      hasPart: subcategories.map((subcat) => ({
        "@type": "WebPage",
        name: subcat.title,
        url: `https://www.promotionalproductsnow.au/category/${category.slug}`,
        description: subcat.description,
      })),
    }),
    mainEntity: [productList],
  };
};

// Extracted helper to generate product schema (based on productPageSchema)
const getProductSchema = (product: ProductObject, url?: string) => {
  // Helper to get detail by name
  const getDetail = (name: string) =>
    product.product.details?.find((detail) => detail.name?.toLowerCase() === name.toLowerCase())
      ?.detail;

  // Helper to get first available value from a list of possible keys
  const getFirstAvailable = (keys: string[]) => keys.map(getDetail).find((val) => !!val);

  function parseMeasurements(measureStr?: string) {
    if (!measureStr) return undefined;
    const parts = measureStr
      .split(/,|\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    const measurements = [];
    for (const part of parts) {
      const [label, values] = part.split(":").map((s) => s.trim());
      if (values) {
        const dims = values.split("x").map((s) => s.trim());
        for (const dim of dims) {
          const match = dim.match(/([\d.]+)\s*(mm|cm|m|in|inch|inches|L|ml|g|kg)?\s*([A-Za-z]*)/i);
          if (match) {
            const value = parseFloat(match[1]);
            const unit = match[2] || undefined;
            const name = match[3] ? `${label} ${match[3]}`.trim() : label;
            measurements.push({
              "@type": "QuantitativeValue",
              name,
              value,
              unitCode: unit ? unit.toUpperCase() : undefined,
            });
          } else {
            measurements.push({
              "@type": "PropertyValue",
              name: label,
              value: dim,
            });
          }
        }
      } else {
        const match = part.match(/([\d.]+)\s*(mm|cm|m|in|inch|inches|L|ml|g|kg)?\s*([A-Za-z]*)/i);
        if (match) {
          const value = parseFloat(match[1]);
          const unit = match[2] || undefined;
          const name = match[3] ? match[3] : undefined;
          measurements.push({
            "@type": "QuantitativeValue",
            name,
            value,
            unitCode: unit ? unit.toUpperCase() : undefined,
          });
        } else {
          measurements.push({
            "@type": "PropertyValue",
            value: part,
          });
        }
      }
    }
    return measurements.length > 0 ? measurements : undefined;
  }

  return {
    "@type": "Product",
    name: product.overview?.name,
    image: product.overview?.heroImage,
    description: product.product?.description,
    sku: product.overview?.code,
    color: product.product.colours?.list
      ?.map((color) => color?.name)
      .filter(Boolean)
      .join(", "),
    keywords: getFirstAvailable(["keywords", "keyword"]),
    material: getFirstAvailable(["material", "materials"]),
    category: product.category?.name || getDetail("category"),
    brand: product.supplier?.name
      ? {
          "@type": "Brand",
          name: product.supplier.name,
        }
      : undefined,
    hasMeasurement:
      parseMeasurements(getFirstAvailable(["size", "itemSize", "Size"])) ||
      getFirstAvailable(["size", "itemSize", "Size"]),
    offers: {
      "@type": "Offer",
      url: url || "",
      priceCurrency: "AUD",
      price: product.userTypePrice?.[UserType.Non_VIP]?.min,
      availability: product.meta?.discontinued
        ? "https://schema.org/Discontinued"
        : "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      "priceSpecification": {
        "@type": "PriceSpecification",
        price: product.userTypePrice?.[UserType.Non_VIP]?.min,
        "priceCurrency": "AUD"
      }
    },
  };
};

export const productPageSchema = (product: ProductObject, url?: string) => {
  // Helper to get detail by name
  const getDetail = (name: string) =>
    product.product.details?.find((detail) => detail.name?.toLowerCase() === name.toLowerCase())
      ?.detail;

  // Helper to get first available value from a list of possible keys
  const getFirstAvailable = (keys: string[]) => keys.map(getDetail).find((val) => !!val);

  // Helper to parse measurement strings into QuantitativeValue objects
  function parseMeasurements(measureStr?: string) {
    if (!measureStr) return undefined;
    // Example: "Barrel: 134mmL x 13mm Diameter, Grip: 12mm Diameter"
    const parts = measureStr
      .split(/,|\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    const measurements = [];
    for (const part of parts) {
      // Try to match: Label: value unit x value unit ...
      const [label, values] = part.split(":").map((s) => s.trim());
      if (values) {
        // Split by 'x' for multiple dimensions
        const dims = values.split("x").map((s) => s.trim());
        for (const dim of dims) {
          // Match value and unit (e.g., 134mmL, 13mm Diameter)
          const match = dim.match(/([\d.]+)\s*(mm|cm|m|in|inch|inches|L|ml|g|kg)?\s*([A-Za-z]*)/i);
          if (match) {
            const value = parseFloat(match[1]);
            const unit = match[2] || undefined;
            const name = match[3] ? `${label} ${match[3]}`.trim() : label;
            measurements.push({
              "@type": "QuantitativeValue",
              name,
              value,
              unitCode: unit ? unit.toUpperCase() : undefined,
            });
          } else {
            // Fallback: just push as a PropertyValue
            measurements.push({
              "@type": "PropertyValue",
              name: label,
              value: dim,
            });
          }
        }
      } else {
        // No label, just try to parse the part
        const match = part.match(/([\d.]+)\s*(mm|cm|m|in|inch|inches|L|ml|g|kg)?\s*([A-Za-z]*)/i);
        if (match) {
          const value = parseFloat(match[1]);
          const unit = match[2] || undefined;
          const name = match[3] ? match[3] : undefined;
          measurements.push({
            "@type": "QuantitativeValue",
            name,
            value,
            unitCode: unit ? unit.toUpperCase() : undefined,
          });
        } else {
          measurements.push({
            "@type": "PropertyValue",
            value: part,
          });
        }
      }
    }
    return measurements.length > 0 ? measurements : undefined;
  }

  return {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: product.overview?.name,
    image: product.overview?.heroImage,
    description: product.product?.description,
    sku: product.overview?.code,
    color: product.product.colours?.list
      ?.map((color) => color?.name)
      .filter(Boolean)
      .join(", "),
    keywords: getFirstAvailable(["keywords", "keyword"]),
    material: getFirstAvailable(["material", "materials"]),
    category: product.category?.name || getDetail("category"),
    brand: product.supplier?.name
      ? {
          "@type": "Brand",
          name: product.supplier.name,
        }
      : undefined,
    hasMeasurement:
      parseMeasurements(getFirstAvailable(["size", "itemSize", "Size"])) ||
      getFirstAvailable(["size", "itemSize", "Size"]),
    offers: {
      "@type": "Offer",
      url: url || "",
      priceCurrency: "AUD",
      price: product.userTypePrice?.[UserType.Non_VIP]?.min,
      availability: product.meta?.discontinued
        ? "https://schema.org/Discontinued"
        : "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      "priceSpecification": {
        "@type": "PriceSpecification",
        price: product.userTypePrice?.[UserType.Non_VIP]?.min,
        "priceCurrency": "AUD"
      }
    },
    // Uncomment and adapt if you have rating data
    // ...(averageRate > 0 && {
    //   aggregateRating: {
    //     "@type": "AggregateRating",
    //     ratingValue: averageRate,
    //     reviewCount: product.product?.reviews?.length || 1,
    //   },
    // }),
  };
};
